import { DispatchRateControl, ConcurrencyLimit } from 'ayvri-std/lib/async';
import { bytesPerPoint3_1 as abfBytesPerPoint31 } from 'ayvri-bin-fmt/lib/abf3';
import TrackLoaderWorker from './workers/trackLoader.worker';
import { downloadFrames } from './helpers/downloadTrackPoints';

// The muxer response size is limited. Compute the maximum number of points we can request
const kMaxPayloadSize = 4 * 1024 * 1024;
const kMaxPointCountPerFrame = 64 * 1024;
const kMaxQuerySize = 8 * 1024;
const kMaxQueriesInFlight = 3;
const kQueryDelayTimeSec = 1.5;
const kDigestChunkLength = 64;

class FrameQuery {
  constructor() {
    const kFrameOverheadSize = 650;

    this.size = kFrameOverheadSize;
    this.frames = {};
    this.readyTime = undefined;
  }

  addFrame(activity, frame, pointSize) {
    const prevFrame = this.frames[activity.activityId] || { pointOffset: null, pointCount: 0 };
    this.frames[activity.activityId] = {
      activity,
      pointOffset: prevFrame.pointOffset === null ? frame.pointOffset : prevFrame.pointOffset,
      pointCount: prevFrame.pointCount + frame.pointCount,
      timestamp: frame.timestamp,
    };

    this.size += pointSize * frame.pointCount;
  }

  canAddFrame(activity, frame, pointSize) {
    const prevFrame = this.frames[activity.activityId] || { pointOffset: null, pointCount: 0 };
    const newPointCount = prevFrame.pointCount + frame.pointCount;
    const newSize = this.size + pointSize * frame.pointCount;
    return newPointCount <= kMaxPointCountPerFrame && newSize <= kMaxPayloadSize && this.querySize <= kMaxQuerySize;
  }

  get activities() {
    return Object.keys(this.frames).map(f => this.frames[f].activity);
  }

  get offsets() {
    return Object.keys(this.frames).map(f => this.frames[f].pointOffset);
  }

  get counts() {
    return Object.keys(this.frames).map(f => this.frames[f].pointCount);
  }

  get querySize() {
    const kQueryOverheadSize = 128;
    const kFrameQueryOverheadSize = 36;
    const frameIds = Object.keys(this.frames);
    let size = kQueryOverheadSize + kFrameQueryOverheadSize * frameIds.length;
    frameIds.forEach(f => {
      const frame = this.frames[f];
      size += frame.activity.activityIdx.toString().length;
    });
    return size;
  }
}

export default class TrackLoader {
  constructor(enableAbf3) {
    this.promises = {};
    this.callbacks = {};
    this.promiseId = 0;
    this.endpoint = undefined;
    this.enableAbf3 = enableAbf3;

    this.sceneDigest = null;

    // *Sigh* [WML 09/19/18]
    if (!process.env.LOADED_MOCHA_OPTS) {
      this.worker = new TrackLoaderWorker();
      this.worker.addEventListener('message', e => onWorkerDataReady(e, this.callbacks, this.promises));
    }
  }

  buildSceneDigest(trackDigests, activities) {
    // The digest timestamps start at 0, so bias by the activities start time to get real time
    trackDigests.forEach(d =>
      d.frames.forEach((f, j) => {
        f.timestamp += activities[d.id].startTime;
        f.startTime = j > 0 ? d.frames[j - 1].timestamp : activities[d.id].startTime;
      }),
    );

    // Construct the scene digest, which is the collection of queries we'll make to the muxer
    // to download the frames of all the tracks. We can only scrub to a time in the scene if we
    // have points from every track up to that time. So we always want to be fetching the frames
    // that have the earliest point times. [WML 10/31/18]
    const sceneDigest = [];
    let track = trackDigests[0];
    while (trackDigests.length) {
      // Add queries to the scene digest until we've accounted for all the frames in all the tracks
      const query = new FrameQuery();

      // Add frame requests to the query until the response will be too large
      while (track && query.canAddFrame(activities[track.id], track.frames[0], track.bytesPerPoint)) {
        // Remove the oldest frame from the digest, and add it to the query
        const oldestFrame = track.frames.shift();
        query.addFrame(activities[track.id], oldestFrame, track.bytesPerPoint);

        // If the track digest doesn't have any more frames, remove it from the list
        trackDigests = trackDigests.filter(t => t.frames[0]);

        // The scene now has all points loaded until the start time of the earliest unloaded frame
        query.readyTime = trackDigests.reduce((a, c) => Math.min(a, c.frames[0].startTime), Infinity);

        // Find the least up to date frame across all tracks
        track = trackDigests.reduce((a, c) => (a.frames[0].startTime < c.frames[0].startTime ? a : c), trackDigests[0]);
      }

      sceneDigest.push(query);
    }
    return sceneDigest;
  }

  onSceneLoad(scene, dispatch) {
    this.endpoint = scene.muxerEndpoint;
    const actArray = Object.keys(scene.activities).map(k => scene.activities[k]);
    const abf3Tracks = this.useAbf3() ? actArray.filter(act => act.abf !== undefined) : [];
    const empty = new Array(abf3Tracks.length).fill(0);

    // Download the track digests (splitting the requests so we don't blow the query length)
    const digestRequestCount = Math.ceil(abf3Tracks.length / kDigestChunkLength);
    const digestPromises = [];
    const digestTracks = [];
    for (let i = 0; i < digestRequestCount; i++) {
      digestTracks[i] = abf3Tracks.slice(i * kDigestChunkLength, (i + 1) * kDigestChunkLength);
      digestPromises[i] = downloadFrames(this.endpoint, digestTracks[i], empty, empty, true);
    }

    this.digestPromise = Promise.all(digestPromises)
      .then(dataChunks => {
        // Combine all the response requests in case they were split
        const trackDigests = [];
        for (let i = 0; i < dataChunks.length; i++) {
          const offset = i * kDigestChunkLength;
          const digests = dataChunks[i].digests.map((d, j) => ({
            id: abf3Tracks[j + offset].activityId,
            frames: d.frames,
            bytesPerPoint: d.bytesPerPoint,
          }));
          Array.prototype.push.apply(trackDigests, digests);
        }
        return trackDigests;
      })
      .catch(e => {
        console.error('Failed to download track digests:', e);
        dispatch({ type: 'FATAL_ERROR', error: 'SCENE_LOAD_FAILURE' });
      });
  }

  onActivityAdded(activity, store, onPointsAdded) {
    const isInScene = store.getState().scene.activities[activity.activityId] !== undefined;

    // If a non-live activity is added, start loading the track points
    // @NOTE: Currently the only way for this to happen is via Studio [WML 04/09/2019]
    if (activity.pathUrl && !isInScene) {
      this.downloadTrack(activity, result => {
        onPointsAdded(activity.activityId, result);
      });
    }
  }

  beginTrackLoading(store, tracks, onPointsAdded) {
    const state = store.getState();
    console.assert(this.sceneDigest === null, 'beginTrackLoading should only be called once');

    // Download tracks for all original scene activities that have a valid download path
    const actArray = Object.keys(state.scene.activities).map(k => state.activities[k]);
    const abf2Tracks = !this.useAbf3()
      ? actArray
      : actArray.filter(act => act.pathUrl !== undefined && act.abf === undefined);

    function loadTrackFrame(result) {
      if (!result.trackVecs) return;
      const activity = actArray.find(a => a.activityId === result.activityId);
      const totalPointCount = activity.numPoints;
      const pointCount = result.trackVecs.cumulative.length;
      const currentPointCount = tracks.getTrack(activity.activityId).length + pointCount;
      const progress = totalPointCount ? currentPointCount / totalPointCount : 1.0;
      store.dispatch({
        type: 'UPDATE_TRACK_LOADING_PROGRESS',
        trackId: activity.activityId,
        progress,
        bytesDownloaded: result.bytesDownloaded,
      });

      onPointsAdded(activity.activityId, result);
    }

    // Load all the track frames for the scene with limited concurrency
    this.digestPromise.then(trackDigests => {
      this.sceneDigest = this.buildSceneDigest(trackDigests, state.activities);

      const limit = new ConcurrencyLimit(kMaxQueriesInFlight);
      const rateControl = new DispatchRateControl(1.0 / kQueryDelayTimeSec, 100, true);
      for (let i = 0; i < this.sceneDigest.length; i++) {
        const frame = this.sceneDigest[i];
        const fetchOp = () =>
          this.downloadFrames(frame.activities, frame.offsets, frame.counts, result => loadTrackFrame(result)).catch(
            e => console.error(`Frame ${i}: ${e}`),
          );

        // Make a request every 1.5 seconds, with no more than kMaxQueriesInFlight running at once
        limit.run(() => rateControl.enqueue(fetchOp));
      }
    });

    abf2Tracks.forEach(activity => {
      const trackId = activity.activityId;
      this.downloadTrack(activity, result => {
        const totalPointCount = activity.numPoints;
        const pointCount = result.trackVecs.cumulative.length;
        const currentPointCount = tracks.getTrack(trackId).length + pointCount;
        const progress = totalPointCount ? currentPointCount / totalPointCount : 1.0;
        store.dispatch({
          type: 'UPDATE_TRACK_LOADING_PROGRESS',
          trackId,
          progress,
          bytesDownloaded: result.bytesDownloaded,
        });

        onPointsAdded(activity.activityId, result);
      })
        .catch(error => {
          console.error('Failed to download track:', error);
        })
        .then(() => {
          // @NOTE: Even if points were unable to load or process, set the progress to complete to avoid waiting
          store.dispatch({ type: 'UPDATE_TRACK_LOADING_PROGRESS', trackId, progress: 1.0 });
        });
    });
  }

  downloadFrames(activities, pointOffsets, pointCounts, onPointsReadyCallback) {
    console.assert(this.endpoint && activities[0].abf, 'Frames only supported for ABF3 tracks');
    return new Promise((resolve, reject) => {
      const promiseId = this.promiseId++;
      this.promises[promiseId] = { resolve, reject };
      this.callbacks[promiseId] = onPointsReadyCallback;
      this.worker.postMessage({
        type: 'downloadFrames',
        endpoint: this.endpoint,
        activities,
        pointOffsets,
        pointCounts,
        promiseId,
      });
    });
  }

  downloadTrack(activity, onPointsReadyCallback) {
    if (this.useAbf3() && activity.abf) {
      const frameSize = activity.abf.maxChunkSize;

      // Request the largest ABF3 frames we can
      const queries = [];
      let pointCount = 0;
      while (pointCount < activity.numPoints) {
        const query = new FrameQuery();
        queries.push(query);

        const bytesPerPoint = abfBytesPerPoint31;
        while (query.canAddFrame(activity, { pointCount: frameSize }, bytesPerPoint)) {
          query.addFrame(activity, { pointCount: frameSize, pointOffset: pointCount }, bytesPerPoint);
          pointCount += frameSize;
          if (pointCount >= activity.numPoints) break;
        }
      }

      // Send the requests to the muxer, pass on processed points
      const promises = queries.map(q =>
        this.downloadFrames(q.activities, q.offsets, q.counts, pointData => onPointsReadyCallback(pointData)).catch(
          () => {},
        ),
      );

      // Once all frames have downloaded and processed, return the points
      return Promise.all(promises);
    } else {
      // Download entire ABF2 at once
      return new Promise((resolve, reject) => {
        const promiseId = this.promiseId++;
        this.promises[promiseId] = { resolve, reject };
        this.callbacks[promiseId] = onPointsReadyCallback;
        this.worker.postMessage({ type: 'downloadPoints', activity, promiseId });
      });
    }
  }

  addPoints(activity, points, onPointsReadyCallback) {
    // This promise will resolve when the points have finished processing
    return new Promise((resolve, reject) => {
      const promiseId = this.promiseId++;
      this.promises[promiseId] = { resolve, reject };
      this.callbacks[promiseId] = onPointsReadyCallback;
      this.worker.postMessage({ type: 'addPoints', activity, promiseId, points });
    });
  }

  useAbf3() {
    return this.enableAbf3 && this.endpoint;
  }
}

// @TODO: Once we are completely using ABF3, remove callbacks and only rely on promises.
//        This function would then look like:
//        function onWorkerResultReady(msg, promises) {
//          const error = msg.data.error;
//          const result = msg.data.result;
//
//          // Grab the pending promise and remove from our cache
//          const promise = promises[msg.data.promiseId];
//          delete promises[msg.data.promiseId];
//
//          if (error) {
//            promise.reject(error);
//          } else {
//            promise.resolve(result);
//          }
//        }
function onWorkerDataReady(msg, callbacks, promises) {
  const isComplete = msg.data.isComplete;
  const error = msg.data.error;
  const callback = callbacks[msg.data.promiseId];

  const result = msg.data.pointData;

  if (!error) callback(result);

  if (isComplete) {
    // Grab the pending promise and remove from our cache
    const promise = promises[msg.data.promiseId];
    delete promises[msg.data.promiseId];
    delete callbacks[msg.data.promiseId];

    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(result);
    }
  }
}
